import { Button, Flex, Select } from '@backstage/ui';
import {
  RiCloseLine,
  RiTeamFill,
  RiRefreshLine,
  RiShapesFill,
} from '@remixicon/react';
import {
  EMPTY_FILTERS,
  hasActiveFilters,
  type Filters,
  type FilterOption,
  type FilterOptionOrSection,
} from './filters';

type FilterBarProps = {
  /** Catalog groups that own the evaluated entities, optionally sectioned. */
  entityOwnerOptions: FilterOptionOrSection[];
  selectedEntityOwners: string[];
  onEntityOwnersChange: (refs: string[]) => void;
  entityKindOptions: FilterOption[];
  lifecycleOptions: FilterOption[];
  value: Filters;
  onChange: (value: Filters) => void;
};

const toSelectedValues = (keys: unknown): string[] =>
  Array.isArray(keys) ? keys.map(String) : [];

const smallSelectStyle = { flex: 1, minWidth: 0 };
const ownerSelectStyle = { flex: 2, minWidth: 0 };

/**
 * Filter controls shown on the check and track detail pages. Renders
 * multi-selects for the entity owner, entity kind and lifecycle. The
 * entity-owner selection is URL-backed by the caller so shared links reproduce
 * it, and the current user's teams are grouped under a "My team" section.
 */
export const FilterBar = ({
  entityOwnerOptions,
  selectedEntityOwners,
  onEntityOwnersChange,
  entityKindOptions,
  lifecycleOptions,
  value,
  onChange,
}: FilterBarProps) => {
  const hasAnySelection =
    hasActiveFilters(value) || selectedEntityOwners.length > 0;

  const handleClearAll = () => {
    onEntityOwnersChange([]);
    onChange(EMPTY_FILTERS);
  };

  return (
    <Flex align="center" gap="4" style={{ width: '100%', flexWrap: 'wrap' }}>
      <Select
        placeholder="Entity owner"
        selectionMode="multiple"
        searchable
        options={entityOwnerOptions}
        value={selectedEntityOwners}
        icon={<RiTeamFill />}
        onChange={keys => onEntityOwnersChange(toSelectedValues(keys))}
        style={ownerSelectStyle}
      />
      <Select
        placeholder="Entity kind"
        selectionMode="multiple"
        options={entityKindOptions}
        value={value.entityKinds}
        icon={<RiShapesFill />}
        onChange={keys =>
          onChange({ ...value, entityKinds: toSelectedValues(keys) })
        }
        style={smallSelectStyle}
      />
      <Select
        placeholder="Lifecycle"
        selectionMode="multiple"
        options={lifecycleOptions}
        value={value.lifecycles}
        icon={<RiRefreshLine />}
        onChange={keys =>
          onChange({ ...value, lifecycles: toSelectedValues(keys) })
        }
        style={smallSelectStyle}
      />
      <Button
        variant="tertiary"
        iconStart={<RiCloseLine />}
        isDisabled={!hasAnySelection}
        onClick={handleClearAll}
      >
        Clear
      </Button>
    </Flex>
  );
};
